const EXERCISE_DOMAINS = ['exercise.com', 'exercise.test'] as const;

function extractHostname(url: string): string | null {
  try {
    const parsed = new URL(url, window.location.origin);
    return parsed.hostname;
  } catch {
    return null;
  }
}

function isExerciseDomain(hostname: string): boolean {
  return EXERCISE_DOMAINS.some(
    (domain) => hostname === domain || hostname.endsWith(`.${domain}`)
  );
}

function getAllowedHosts(options?: SafeRedirectOptions): Set<string> {
  const hosts = new Set<string>();

  // Current origin is always allowed — this inherently covers the
  // white-label domain the user is currently visiting.
  hosts.add(window.location.hostname);

  // Session-based white-label domains
  if (options?.sessionHost) {
    hosts.add(options.sessionHost);
  }
  if (options?.sessionAlternateHost) {
    hosts.add(options.sessionAlternateHost);
  }

  // Extract hostnames from environment variable URLs
  const envUrls = [
    import.meta.env.VITE_EXERCISE_BASE_URL,
    import.meta.env.VITE_RAILS_BASE_URL,
    import.meta.env.VITE_V3_BASE_URL,
  ].filter(Boolean);

  for (const envUrl of envUrls) {
    const hostname = extractHostname(envUrl);
    if (hostname) hosts.add(hostname);
  }

  // Extra hosts provided by caller
  if (options?.extraAllowedHosts) {
    for (const h of options.extraAllowedHosts) {
      if (h) hosts.add(h);
    }
  }

  return hosts;
}

export interface SafeRedirectOptions {
  /** Platform.host from the current session (white-label custom domain) */
  sessionHost?: string;
  /** Platform.alternateHost from the current session */
  sessionAlternateHost?: string;
  /** Any additional hostnames to allow */
  extraAllowedHosts?: string[];
}

export function isUrlSafe(
  url: string | null | undefined,
  options?: SafeRedirectOptions
): boolean {
  if (!url) return false;

  const trimmed = url.trim();
  if (!trimmed) return false;

  // Block protocol-relative URLs (//evil.com)
  if (trimmed.startsWith('//')) return false;

  // Allow relative paths (must start with /)
  if (trimmed.startsWith('/')) return true;

  // Parse and validate absolute URLs
  try {
    const parsed = new URL(trimmed, window.location.origin);

    // Block dangerous protocols
    const dangerousProtocols = ['javascript:', 'data:', 'vbscript:'];
    if (dangerousProtocols.includes(parsed.protocol)) return false;

    // Only allow http/https
    if (parsed.protocol !== 'http:' && parsed.protocol !== 'https:') {
      return false;
    }

    const hostname = parsed.hostname;

    // Exercise.com and all subdomains are always allowed
    if (isExerciseDomain(hostname)) return true;

    // Check against dynamic allowlist
    const allowedHosts = getAllowedHosts(options);
    return allowedHosts.has(hostname);
  } catch {
    return false;
  }
}

export function safeRedirect(
  url: string | null | undefined,
  fallback: string = '/',
  options?: SafeRedirectOptions
): void {
  const target = isUrlSafe(url, options) ? url! : fallback;
  window.location.href = target;
}

export function getSafeRedirectUrl(
  url: string | null | undefined,
  fallback: string = '/',
  options?: SafeRedirectOptions
): string {
  return isUrlSafe(url, options) ? url! : fallback;
}
